import { useCallback } from 'react';
import { useRBACContext } from "./provider";
export var useRBAC = function useRBAC() {
  var _useRBACContext = useRBACContext(),
    applicationRolesSet = _useRBACContext.applicationRolesSet,
    applicationProductsSet = _useRBACContext.applicationProductsSet,
    applicationPermissionsSet = _useRBACContext.applicationPermissionsSet,
    applicationPermissionsLength = _useRBACContext.applicationPermissionsLength,
    isSuperAdminUser = _useRBACContext.isSuperAdminUser;
  var hasRoles = useCallback(function (roles, _ref) {
    var operator = _ref.operator;
    if (operator === 'AND') {
      return roles.every(function (role) {
        return applicationRolesSet.has(role);
      });
    }
    return roles.some(function (role) {
      return applicationRolesSet.has(role);
    });
  }, [applicationRolesSet]);
  var hasProducts = useCallback(function (products, _ref2) {
    var operator = _ref2.operator;
    if (operator === 'AND') {
      return products.every(function (product) {
        return applicationProductsSet.has(product);
      });
    }
    return products.some(function (product) {
      return applicationProductsSet.has(product);
    });
  }, [applicationProductsSet]);
  var hasPermissions = useCallback(function (permissions, _ref3) {
    var operator = _ref3.operator;
    if (
    /**
     * @security When applicationPermissions is empty, this grants universal access.
     * This typically indicates a super-admin or unrestricted account.
     */
    applicationPermissionsLength === 0 ||
    /**
     * @security When user has the SUPERADMIN role, this grants universal access.
     */
    isSuperAdminUser) {
      return true;
    }
    if (operator === 'AND') {
      return permissions.every(function (permission) {
        return applicationPermissionsSet.has(permission);
      });
    }
    return permissions.some(function (permission) {
      return applicationPermissionsSet.has(permission);
    });
  }, [applicationPermissionsLength, applicationPermissionsSet, isSuperAdminUser]);
  var hasAccess = useCallback(function (config) {
    var roles = config.roles,
      products = config.products,
      permissions = config.permissions,
      requireAll = config.requireAll;
    var checks = [];
    if (roles && roles.values.length > 0) {
      checks.push(hasRoles(roles.values, {
        operator: roles.operator
      }));
    }
    if (products && products.values.length > 0) {
      checks.push(hasProducts(products.values, {
        operator: products.operator
      }));
    }
    if (permissions && permissions.values.length > 0) {
      checks.push(hasPermissions(permissions.values, {
        operator: permissions.operator
      }));
    }
    if (checks.length === 0) {
      return false;
    }
    return requireAll ? checks.every(function (check) {
      return check;
    }) : checks.some(function (check) {
      return check;
    });
  }, [hasRoles, hasProducts, hasPermissions]);

  // Convenience role helpers
  // Once backend has exposed the enums for roles, products and permissions, we can remove these hardcoded string values
  // and use the enums instead
  var isUser = useCallback(function () {
    return hasRoles(['ROLE_USER'], {
      operator: 'AND'
    });
  }, [hasRoles]);
  var isMentor = useCallback(function () {
    return hasRoles(['ROLE_MENTOR'], {
      operator: 'AND'
    });
  }, [hasRoles]);
  var isLeader = useCallback(function () {
    return hasRoles(['ROLE_LEADER'], {
      operator: 'AND'
    });
  }, [hasRoles]);
  var isSupervisor = useCallback(function () {
    return hasRoles(['ROLE_SUPERVISOR'], {
      operator: 'AND'
    });
  }, [hasRoles]);
  var isSuperAdmin = useCallback(function () {
    return hasRoles(['ROLE_SUPERADMIN'], {
      operator: 'AND'
    });
  }, [hasRoles]);
  var isAdmin = useCallback(function () {
    return hasRoles(['ROLE_ADMIN'], {
      operator: 'AND'
    });
  }, [hasRoles]);
  var isStaff = useCallback(function () {
    return hasRoles(['ROLE_STAFF'], {
      operator: 'AND'
    });
  }, [hasRoles]);

  // Product access helpers
  var hasLearningAccess = useCallback(function () {
    return hasProducts(['LEARNING'], {
      operator: 'AND'
    });
  }, [hasProducts]);
  var hasTalentAccess = useCallback(function () {
    return hasProducts(['TALENT'], {
      operator: 'AND'
    });
  }, [hasProducts]);
  var hasAnalyticsAccess = useCallback(function () {
    return hasProducts(['ANALYTICS'], {
      operator: 'AND'
    });
  }, [hasProducts]);
  var hasHRISAccess = useCallback(function () {
    return hasProducts(['HRIS'], {
      operator: 'AND'
    });
  }, [hasProducts]);
  var hasATSAccess = useCallback(function () {
    return hasProducts(['ATS'], {
      operator: 'AND'
    });
  }, [hasProducts]);
  var hasBenefitsAccess = useCallback(function () {
    return hasProducts(['BENEFITS'], {
      operator: 'AND'
    });
  }, [hasProducts]);
  return {
    hasRoles: hasRoles,
    hasProducts: hasProducts,
    hasPermissions: hasPermissions,
    hasAccess: hasAccess,
    // Convenience helpers
    isUser: isUser,
    isAdmin: isAdmin,
    isSuperAdmin: isSuperAdmin,
    isMentor: isMentor,
    isLeader: isLeader,
    isSupervisor: isSupervisor,
    isStaff: isStaff,
    // Product helpers
    hasLearningAccess: hasLearningAccess,
    hasTalentAccess: hasTalentAccess,
    hasAnalyticsAccess: hasAnalyticsAccess,
    hasHRISAccess: hasHRISAccess,
    hasATSAccess: hasATSAccess,
    hasBenefitsAccess: hasBenefitsAccess
  };
};